import { useEffect, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, Check, Users } from "lucide-react";
import { getBedIcon, resolveAmenities, resolveRoom } from "@/data/rooms";
import { loadRooms } from "@/data/roomStore";
import { useSEO } from "@/hooks/useSEO";
import { useLang } from "@/i18n";
import BookingForm from "@/components/BookingForm";
import { Reveal } from "@/components/Reveal";

export default function RoomPage() {
    const { slug } = useParams();
    const [room, setRoom] = useState(null);
    const [loaded, setLoaded] = useState(false);
    const [active, setActive] = useState(0);
    const { lang, t } = useLang();

    useEffect(() => {
        setActive(0);
        setLoaded(false);
        loadRooms()
            .then((list) => {
                setRoom(list.find((r) => r.slug === slug) || null);
                setLoaded(true);
            })
            .catch(() => setLoaded(true));
    }, [slug]);

    const d = room ? resolveRoom(room, lang) : null;

    useSEO(
        room ? `${d.name}${t.seo.roomSuffix}` : t.seo.notFoundTitle,
        room
            ? t.seo.roomDesc.replace("{name}", d.name).replace("{capacity}", d.capacity).replace("{beds}", d.beds)
            : t.seo.notFoundDesc,
    );

    if (!loaded) return <div className="min-h-screen bg-cream" data-testid="room-loading" />;
    if (!room) return <Navigate to="/" replace />;

    const BedIcon = getBedIcon(room);
    const amenityLabel = (a) => (lang === "en" ? a.labelEn || a.label : a.label);
    const items = [{ src: room.photo, alt: `${d.name} — ${d.beds}` }, ...room.gallery];

    return (
        <div data-testid={`room-page-${room.slug}`} className="pt-28 md:pt-32">
            <div className="mx-auto max-w-7xl px-6 md:px-12">
                <Reveal y={14}>
                    <Link
                        to="/alojamientos"
                        data-testid="back-to-rooms-button"
                        className="inline-flex items-center gap-2 text-sm font-medium text-olive transition-all duration-300 hover:gap-3 hover:text-olive-dark"
                    >
                        <ArrowLeft className="h-4 w-4" strokeWidth={1.5} />
                        {t.roomPage.back}
                    </Link>
                </Reveal>

                <Reveal delay={0.08} className="mt-8">
                    <div className="relative">
                        <AnimatePresence mode="wait">
                            <motion.div
                                key={active}
                                initial={{ opacity: 0, scale: 1.02 }}
                                animate={{ opacity: 1, scale: 1 }}
                                exit={{ opacity: 0 }}
                                transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                            >
                                <img
                                    src={items[active].src}
                                    alt={items[active].alt}
                                    width="1200"
                                    height="800"
                                    data-testid={`room-gallery-main-${room.slug}`}
                                    className="h-[46vh] w-full rounded-3xl object-cover md:h-[62vh]"
                                />
                            </motion.div>
                        </AnimatePresence>
                    </div>
                    <div className="mt-4 grid grid-cols-4 gap-3" role="tablist" aria-label={t.roomPage.galleryAria}>
                        {items.map((item, i) => (
                            <button
                                key={i}
                                type="button"
                                role="tab"
                                aria-selected={active === i}
                                aria-label={`${t.roomPage.viewImage} ${i + 1}`}
                                onClick={() => setActive(i)}
                                data-testid={`room-gallery-thumb-${room.slug}-${i}`}
                                className={`overflow-hidden rounded-xl transition-all duration-300 ${
                                    active === i
                                        ? "ring-2 ring-amber ring-offset-2 ring-offset-cream"
                                        : "opacity-70 hover:opacity-100"
                                }`}
                            >
                                <img
                                    src={item.src}
                                    alt={item.alt}
                                    loading="lazy"
                                    decoding="async"
                                    width="1200"
                                    height="800"
                                    className="h-16 w-full object-cover md:h-24"
                                />
                            </button>
                        ))}
                    </div>
                </Reveal>

                <div className="mt-14 grid gap-14 pb-24 md:mt-20 md:pb-32 lg:grid-cols-12 lg:gap-16">
                    <div className="lg:col-span-7">
                        <Reveal>
                            <p className="text-xs font-medium uppercase tracking-[0.28em] text-amber">{d.tagline}</p>
                            <h1 className="mt-3 font-display text-4xl font-light leading-[1.05] tracking-tight text-ink sm:text-5xl lg:text-6xl">
                                {d.name}
                            </h1>
                            <p className="mt-6 max-w-2xl text-base font-light leading-relaxed text-ink-muted md:text-lg">
                                {d.description}
                            </p>
                        </Reveal>

                        <Reveal delay={0.12} className="mt-12">
                            <h2 className="text-xs font-medium uppercase tracking-[0.28em] text-olive">{t.roomPage.details}</h2>
                            <div className="mt-5 grid gap-4 sm:grid-cols-2">
                                <div data-testid="room-detail-capacity" className="flex items-center gap-4 rounded-2xl border border-sand-border bg-sand p-5">
                                    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-cream text-olive">
                                        <Users className="h-5 w-5" strokeWidth={1.5} />
                                    </span>
                                    <div>
                                        <p className="text-xs font-medium uppercase tracking-wider text-ink-muted">{t.roomPage.capacity}</p>
                                        <p className="text-sm font-medium text-ink">{d.capacity}</p>
                                    </div>
                                </div>
                                <div data-testid="room-detail-beds" className="flex items-center gap-4 rounded-2xl border border-sand-border bg-sand p-5">
                                    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-cream text-olive">
                                        <BedIcon className="h-5 w-5" strokeWidth={1.5} />
                                    </span>
                                    <div>
                                        <p className="text-xs font-medium uppercase tracking-wider text-ink-muted">{t.roomPage.beds}</p>
                                        <p className="text-sm font-medium text-ink">{d.beds}</p>
                                    </div>
                                </div>
                            </div>
                        </Reveal>

                        <Reveal delay={0.18} className="mt-12">
                            <h2 className="text-xs font-medium uppercase tracking-[0.28em] text-olive">{t.roomPage.amenitiesTitle}</h2>
                            <ul className="mt-5 grid gap-3 sm:grid-cols-2" data-testid="room-amenities-list">
                                {resolveAmenities(room).map((a) => (
                                    <li
                                        key={a.key}
                                        className="flex items-center gap-3 rounded-xl bg-cream px-4 py-3 text-sm font-light text-ink shadow-[0_4px_16px_rgba(45,51,25,0.05)]"
                                    >
                                        <a.icon className="h-4 w-4 shrink-0 text-amber" strokeWidth={1.5} />
                                        {amenityLabel(a)}
                                    </li>
                                ))}
                                <li className="flex items-center gap-3 rounded-xl bg-cream px-4 py-3 text-sm font-light text-ink shadow-[0_4px_16px_rgba(45,51,25,0.05)]">
                                    <Check className="h-4 w-4 shrink-0 text-amber" strokeWidth={1.5} />
                                    {t.roomPage.beachPerk}
                                </li>
                            </ul>
                        </Reveal>
                    </div>

                    <div className="lg:col-span-5">
                        <Reveal delay={0.1}>
                            <BookingForm roomName={d.name} />
                        </Reveal>
                    </div>
                </div>
            </div>
        </div>
    );
}
